import { EXCHANGE_COMMENT_MAX_LENGTH } from '../../constants';

/**
 * How to treat gameDate. The two asset-details save paths differ here and the difference is
 * deliberate, so it is a parameter rather than a choice made for them:
 *
 *  - 'dropAlways'    the exchange panel never sends gameDate.
 *  - 'dropWhenEmpty' the home page panel sends it when it has a value.
 *
 * Both exist to stop a null date being stored as 1/1/1970 in mongo.
 */
export type GameDateMode = 'dropAlways' | 'dropWhenEmpty';

export interface PrepareAssetUpdatesOptions {
  /** Stamped as modifiedBy. */
  displayName?: string;
  gameDateMode: GameDateMode;
}

export interface PrepareAssetUpdatesResult {
  /** Ready to send. Absent when `error` is set. */
  updates?: Record<string, any>;
  /** User-facing message; the caller surfaces it and abandons the save. */
  error?: string;
}

/**
 * The part both asset-details save paths share: validate, normalise and stamp a set of asset
 * updates before they go to vault-api.
 *
 * There are two panels - the home page one and the exchange search one - each with its own
 * near-identical updateAsset thunk. Keeping this logic in one place is not cosmetic: VV-226's
 * comment-length check was added to only one of them and the omission was found in review, and
 * anything added here in future would have had the same coin-flip.
 *
 * Returns a new object rather than mutating the argument. Both thunks previously mutated the
 * updates held in redux state, which is a defect independent of this refactor.
 */
export const prepareAssetUpdates = (
  assetUpdates: Record<string, any>,
  { displayName, gameDateMode }: PrepareAssetUpdatesOptions,
): PrepareAssetUpdatesResult => {
  // VV-226: vault-api rejects an over-length comment with a 400, which would otherwise surface
  // as a generic save failure with the edit still open and nothing naming the field at fault.
  const comment = assetUpdates.exchangeComment;
  if (typeof comment === 'string' && comment.trim().length > EXCHANGE_COMMENT_MAX_LENGTH) {
    return {
      error: `Comment is ${comment.trim().length} characters; the maximum is ${EXCHANGE_COMMENT_MAX_LENGTH}. Shorten it to save.`,
    };
  }

  const updates: Record<string, any> = { ...assetUpdates };

  // Send what was validated. The cap is measured on the trimmed value because that is what
  // the API stores, so sending the raw string would let leading or trailing whitespace carry
  // a payload past a check that passed.
  if (typeof comment === 'string') {
    updates.exchangeComment = comment.trim();
  }

  Object.keys(updates).forEach((key) => {
    if (key === 'isColor' || key === 'hasBroadcastGraphics') {
      updates[key] = updates[key] === true;
    }
  });

  if (gameDateMode === 'dropAlways' || !updates.gameDate) {
    delete updates.gameDate;
  }

  updates.modifiedBy = displayName;
  updates.modifiedDate = new Date().toISOString();

  return { updates };
};
